import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import SingleSelect from '../../../components/atoms/forms/SingleSelect';
import MultiSelect from '../../../components/atoms/forms/MultiSelect';
import Input from '../../../components/atoms/forms/Input';

// Shortage detail subform for HeadcountSection: actual count + reason +
// affected routes for one resource kind (driver / van). Labels, ids, and
// test ids all derive from `kind`, so both variants — and any future third
// resource — render from the same markup. Test ids keep the section's
// `hc-` prefix so HeadcountSection tests target them unchanged.

const KIND_LABELS = {
  driver: 'driver',
  van: 'van',
};

function capitalize(word) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

const StyledDiv = styledComponent('div', {
  '& .ss-shortage': {
    background: theme.colors.warningBg,
    borderLeft: `4px solid ${theme.colors.warning}`,
    padding: theme.spacing.bodyPadding,
    borderRadius: theme.radii.input,
    marginBottom: theme.spacing.fieldGap,
  },
  '& .ss-shortage-title': {
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.warningText,
    marginBottom: theme.spacing.fieldGap,
  },
  '& .ss-field': {
    marginBottom: theme.spacing.fieldGap,
  },
  '& .ss-label': {
    display: 'block',
    fontSize: theme.typography.label.fontSize,
    fontWeight: theme.fontWeights.medium,
    color: theme.colors.text,
    marginBottom: theme.spacing.inputLabelGap,
  },
});

function ShortageSubform({
  kind,
  count,
  onCountChange,
  reason,
  onReasonChange,
  reasonOptions,
  routes,
  onRoutesChange,
  routeOptions,
}) {
  const noun = KIND_LABELS[kind];
  const countId = `hc-actual-${noun}s`;
  const reasonId = `hc-${noun}-reason`;

  const routesValue = useMemo(
    () => (routes || []).map((name) => ({ value: name, label: name })),
    [routes],
  );

  return (
    <StyledDiv>
      <div className="ss-shortage" data-testid={`hc-${noun}-shortage`}>
        <div className="ss-shortage-title">
          ⚠️ {capitalize(noun)} Shortage Details
        </div>
        <div className="ss-field">
          <label className="ss-label" htmlFor={countId}>
            Actual {noun} count
          </label>
          <Input
            id={countId}
            type="number"
            min="0"
            step="1"
            placeholder="Enter actual count"
            value={count}
            onChange={(e) => onCountChange(e.target.value)}
            dataTestId={countId}
          />
        </div>
        <div className="ss-field">
          <span className="ss-label">Reason {noun} count changed</span>
          <SingleSelect
            inputId={reasonId}
            options={reasonOptions}
            value={reasonOptions.find((o) => o.value === reason) || null}
            onChange={(opt) => onReasonChange(opt ? opt.value : '')}
            placeholder="Select reason…"
            isClearable
            data-testid={reasonId}
          />
        </div>
        <div className="ss-field" data-testid={`hc-${noun}-routes-field`}>
          <span className="ss-label">Routes without a {noun}</span>
          <MultiSelect
            options={routeOptions}
            value={routesValue}
            onChange={(selected) =>
              onRoutesChange((selected || []).map((s) => s.value))
            }
            isDisabled={routeOptions.length === 0}
            width="100%"
          />
        </div>
      </div>
    </StyledDiv>
  );
}

ShortageSubform.propTypes = {
  kind: PropTypes.oneOf(['driver', 'van']).isRequired,
  count: PropTypes.string,
  onCountChange: PropTypes.func.isRequired,
  reason: PropTypes.string,
  onReasonChange: PropTypes.func.isRequired,
  reasonOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ).isRequired,
  routes: PropTypes.arrayOf(PropTypes.string),
  onRoutesChange: PropTypes.func.isRequired,
  routeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
};

ShortageSubform.defaultProps = {
  count: '',
  reason: '',
  routes: [],
  routeOptions: [],
};

export default ShortageSubform;
